/**
 * The follow-up letter: write it on the call, open it in the mail composer.
 *
 * Two calls, and the split is the point rather than an accident of the API. The
 * POST spends a model request; the GET does not. The hand-over to the mail
 * screen happens on a navigation, which means it also happens on a reload, on a
 * back button and on a restored tab - so it must be the free one.
 *
 * ⚠ NEITHER TAKES A RECIPIENT AND NEITHER SHOULD. The address is resolved on
 * the server, on every request, through the records the call is filed against,
 * with the do-not-contact list in front of it. An address that could be sent
 * from here would move that gate into the browser, where it is one refactor
 * away from not running at all.
 */
import { ApiError, api } from "./api";

/**
 * Which record supplied the address. Shown so the operator can judge it.
 * Mirrors `FollowUpAddressSource` (`crm-calls/lib/follow-up-recipient.ts`);
 * "site" is an AI alert call's address the legal gate read off the company's
 * own website (ring R9).
 */
export type FollowUpAddressSource = "contact" | "client" | "lead" | "register" | "site";

export interface CallFollowUpDraft {
  subject: string;
  body: string;
  /** When it was written, ISO. Empty on a draft stored before this was kept. */
  at: string;
  /**
   * Who to send it to, or null.
   *
   * Null is the ordinary answer on a cold call and not a failure: `CallLog` has
   * no e-mail column, so an address only exists if one of the four records
   * behind the call carries one. The composer then opens with an empty box,
   * which is correct - the operator has a business card or they do not.
   */
  address: string | null;
  source: FollowUpAddressSource | null;
  /**
   * The refusal that took an address away, when one did.
   *
   * A different answer from "we do not know one", and worth its own field: an
   * operator who cannot tell the two apart will look the address up by hand and
   * send the letter anyway.
   */
  blocked: { value: string; source: string; reason: string | null } | null;
  /**
   * When the AI already sent this letter to the company itself, and where. Only
   * an AI alert call's letter has them; the composer warns before a second send.
   */
  sentAt?: string;
  sentTo?: string;
}

/**
 * Write the letter. One model request, a few seconds, operator waiting.
 *
 * Refuses with 409 `alert_letter` on an AI alert call (its letter is built by
 * code with the mandatory blocks and is never rewritten by the model), 409
 * `no_summary` on a call nothing has been written about yet,
 * 503 `ai_not_configured` with no key, 429 on a spent daily budget, and 403
 * unless this viewer may HEAR the call - the letter is the conversation in
 * other words.
 */
export const writeCallFollowUp = (callId: string): Promise<CallFollowUpDraft> =>
  api.post<CallFollowUpDraft>(`/admin/crm/calls/${encodeURIComponent(callId)}/follow-up`, {});

/** What the composer opens with. 404 means "press the button first". */
export const getCallFollowUp = (callId: string): Promise<CallFollowUpDraft> =>
  api.get<CallFollowUpDraft>(`/admin/crm/calls/${encodeURIComponent(callId)}/follow-up`);

/**
 * The refusals worth their own sentence, as catalogue keys. Anything else is
 * the generic error.
 */
const WRITE_ERRORS: Readonly<Record<string, string>> = {
  alert_letter: "calls.followUp.alertLetter",
  no_summary: "calls.followUp.noSummary",
  ai_not_configured: "calls.followUp.noKey",
  quota: "calls.followUp.quota",
};

/** A failed write as the catalogue key the toast says. */
export function followUpErrorKey(err: unknown): string {
  const code = err instanceof ApiError ? String((err.body as { error?: unknown } | null)?.error ?? "") : "";
  return WRITE_ERRORS[code] ?? "calls.followUp.failed";
}

/**
 * What the composer says about the letter it opened with, as a catalogue key,
 * or null when there is nothing to say.
 *
 * First, a letter the AI already sent to the company itself: the one warning
 * that stops a second copy going out. Then three different silences about the
 * recipient: the legal gate forbids writing to this company (an AI alert
 * call), the address is on the do-not-contact list, or no address is on file.
 */
export function followUpNoticeKey(draft: Pick<CallFollowUpDraft, "address" | "blocked" | "sentAt">): string | null {
  if (draft.sentAt) return "email.callAlreadySent";
  if (draft.blocked) return draft.blocked.source === "legal_gate" ? "email.callLegalBlocked" : "email.callSuppressed";
  return draft.address ? null : "email.callNoRecipient";
}
